import {connect} from 'pwa-helpers';
import {LitElement, html} from 'lit';
import {customElement} from 'lit/decorators/custom-element.js';
import {msg} from '@lit/localize';
import {navigator, router} from 'lit-element-router';
import deepEqual from 'deep-equal';
import {fetchSubscription, selectSubscriptionById, updateSubscription} from '../../slices/subscriptionSlice';
import {
	SUB_ACTIVE,
	SUB_ACTIVE_PENDING,
	SUB_CANCEL,
	SUB_NONE,
	SUB_PAST_DUE,
	SUB_PAUSED,
	SUB_SCHEDULED_TO_CANCEL,
	SUB_TRIAL_EXPIRED,
	SUB_TRIALING,
} from '../../services/subscription/subscriptionStatus';

import store from '../../store';
import getRoutes from '../../routes';
import formPageStyle from '../../../../scss/formPage.scss';
import linkstyle from '../../../../scss/link.scss';
import loadingSpinnerStyle from '../../../../scss/loadingSpinner.scss';
import style from '../../../../scss/editSubscription.scss';

import {deepClone} from '../../util/deepClone';

@customElement('arc-edit-subscription')
export class EditSubscription extends connect(store)(router(navigator(LitElement))) {
	static styles = [formPageStyle, linkstyle, loadingSpinnerStyle, style];

	static properties = {
		formData: {type: Object, hasChanged: (n, o) => !deepEqual(n, o)},
		errors: {type: Object, hasChanged: (n, o) => !deepEqual(n, o)},
		edit: {type: Boolean},
	};

	constructor() {
		super();
		this.updateNeeded = true;
		this.errors = null;
		this.currentLanguage = 'en';
		this.create = false;
		store.subscribe(() => this.requestUpdate());
	}

	async stateChanged(state) {
		this.state = state;
		this.status = state.status;
		this.errors = state.subscription.error;
		if (this.updateNeeded) {
			this.updateNeeded = false;
			await store.dispatch(fetchSubscription({id: this.subscriptionId})); // fetch sub from backend
			// clone data for local editing (component state)
			this.formData = deepClone(selectSubscriptionById(this.state, this.subscriptionId));
		}
	}

	static get routes() {
		return getRoutes();
	}

	async router(route, params, query, data) {
		this.updateNeeded =
			route === 'admin-subscription' && this.subscriptionId !== params.subscriptionId;
		this.params = params;
		this.subscriptionId = params.subscriptionId;
		this.requestUpdate();
	}

	handleValueChange(fieldData, value) {
		const field = fieldData.field;
		this.formData[field] = value;
		this.requestUpdate();
	}

	async save() {
		const data = Object.keys(this.formData)
			.filter(k => ['id', 'status', 'trialEndsAt', 'scheduledToCancelAt'].includes(k))
			.reduce((acc, key) => ({...acc, [key]: this.formData[key]}), {});
		await store.dispatch(updateSubscription(data));
		this.formData = selectSubscriptionById(this.state, this.subscriptionId);
	}

	formHasChanged() {
		if (!this.formData || !this.subscriptionId) return false;
		const newData = deepClone(this.formData);
		const oldData = deepClone(selectSubscriptionById(this.state, this.subscriptionId));
		delete newData.modified;
		delete oldData.modified;
		return !deepEqual(newData, oldData);
	}

	render() {
		const statusOptions = [
			{value: SUB_TRIALING, label: msg('Currently Trialing')},
			{value: SUB_TRIAL_EXPIRED, label: msg('Trial Has Expired')},
			{value: SUB_ACTIVE, label: msg('Active (paying customer)')},
			{value: SUB_ACTIVE_PENDING, label: msg('Active (payment pending)')},
			{value: SUB_SCHEDULED_TO_CANCEL, label: msg('Active (but has cancelled)')},
			{value: SUB_PAST_DUE, label: msg('Active (but has missed payments)')},
			{value: SUB_CANCEL, label: msg('Canceled (irreversible!!!)')},
			{value: SUB_PAUSED, label: msg('Paused (special cases)')}
		];

		let readOnlyMetadataFields = {
			plan: {label: msg('Plan')},
			billingCycle: {label: msg('Billing Cycle')},
			price: {label: msg('Price')},
			iban: {label: msg('IBAN')},
			accountName: {label: msg('Bank Account Name')},
			name: {label: msg('Name (Invoices)')},
			email: {label: msg('Email (Invoices)')},
			street: {label: msg('Street Address (Invoices)')},
			zip: {label: msg('ZIP (Invoices)')},
			country: {label: msg('Country (Invoices)')},
		};
		readOnlyMetadataFields = Object.keys(readOnlyMetadataFields).reduce((acc, key) => {
			acc.push({
				type: 'textInput',
				title: readOnlyMetadataFields[key].label,
				field: key,
				disabled: true,
				data: this.formData,
				errors: this.errors,
			});
			return acc;
		}, []);

		const inputFields = [
			{
				type: 'select',
				title: msg('Status'),
				field: 'status',
				options: statusOptions.map((option) => ({
					id: option.value,
					name: [{lang: 'en', value: option.label}],
				})),
				multiple: false,
				disabled: this.formData?.status === SUB_CANCEL,
				data: this.formData,
				errors: this.errors,
				required: true,
				handler: this.handleValueChange.bind(this),
				tooltip: 'Once SEPA payment is verified, switch to Active (paying customer). If payments have stopped working, switch to Active (but has missed payments)',
			},
			{
				type: 'textInput',
				title: msg('Trial Ends At'),
				field: 'trialEndsAt',
				data: this.formData,
				placeholder: 'YYYY-MM-DD HH:MM:SS',
				errors: this.errors,
				handler: this.handleValueChange.bind(this),
				tooltip:
					'Do not edit, unless you know what you doing. Keep to the datetime format: YYYY-MM-DD HH:MM:SS',
			},
			{
				type: 'textInput',
				title: msg('Scheduled To Cancel At'),
				field: 'scheduledToCancelAt',
				data: this.formData,
				errors: this.errors,
				disabled: true,
				// handler: this.handleValueChange.bind(this),
				tooltip:
					'If a user cancels their subscription, it must stay active until the end of the current building period. This field records that final cancellation date.',
			},
			...readOnlyMetadataFields,
			{
				type: 'textInput',
				title: msg('Created At'),
				field: 'created',
				disabled: true,
				data: this.formData,
				errors: this.errors,
				required: true,
			},
			{
				type: 'textInput',
				title: msg('Started At'),
				field: 'startedAt',
				disabled: true,
				data: this.formData,
				errors: this.errors,
				required: true,
			},
			{
				type: 'textInput',
				title: msg('All Metadata'),
				field: 'metadata',
				inputValue: this.formData?.metadata,
				multiline: true,
				disabled: true,
				data: this.formData,
				errors: this.errors,
			},
		];

		return html`
			<div class="formPage">
				<arc-toolbar class="toolbar">
					<div class="leftSlot" slot="left">
						<arc-breadcrumbs>
							<arc-routerlink route="admin-subscriptions"> ${msg('Subscriptions')}</arc-routerlink>
							<span>${this.formData?.account?.name}</span>
						</arc-breadcrumbs>
					</div>
					<div class="rightSlot" slot="right">
						<arc-button
							title="${msg('Save Subscription')}"
							type="primary"
							.additionalInfo="${false}"
							?disabled="${!this.formHasChanged()}"
							class="saveButton"
							@click=${this.save}
						></arc-button>
					</div>
				</arc-toolbar>
				<div class="leftRightColumns">
					<div class="leftColumn">
						<section></section>
					</div>
					<div class="rightColumn">
						<section>
							<arc-form-grid
								class="subscriptionInfo"
								.fields=${inputFields}
								.localFormData=${this.formData}
								.errors=${this.errors}
								.edit=${true}
							>
							</arc-form-grid>
						</section>
					</div>
				</div>
			</div>
		`;
	}
}
